import React, { useState } from 'react';
import { Box, Container, Typography, Paper } from '@mui/material';
import PrimaryButton from '../components/PrimaryButton';
import SecondaryButton from '../components/SecondaryButton';

// CSS global para garantir que não haja espaços em branco
const globalStyles = `
  html, body, #root {
    height: 100%;
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }
`;

const PitchPdf: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = '/ThinkWay.pdf';
    link.download = 'ThinkWay.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleOpenInNewTab = () => {
    window.open('/ThinkWay.pdf', '_blank');
  };

  const handlePdfLoad = () => {
    setIsLoading(false);
  };

  const handlePdfError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Aplicar estilos globais
  React.useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = globalStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      styleElement.remove();
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100vh',
        background: 'linear-gradient(135deg, #042E4B 0%, #1A5B7A 100%)',
        py: { xs: 1, md: 2 },
        px: { xs: 1, md: 2 },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Container 
        maxWidth="lg" 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100vh',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: { xs: 2, md: 0 },
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '1.3rem', md: '1.8rem' },
              lineHeight: 1.2,
              flex: 1,
              minWidth: { xs: '100%', md: 'auto' },
              mb: { xs: 1, md: 0 },
            }}
          >
            Pitch PDF ThinkWay - Programa Nascer 2025
          </Typography>
          
          <PrimaryButton
            onClick={handleDownload}
            size="medium"
            sx={{
              px: 3,
              py: 1,
              fontSize: '0.9rem',
              fontWeight: 'bold',
              backgroundColor: '#EF443C',
              minWidth: '160px',
              '&:hover': {
                backgroundColor: '#d73b33',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 12px rgba(239, 68, 60, 0.4)',
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
            }}
          >
            ⬇️ BAIXAR PDF
          </PrimaryButton>
        </Box>

        <Paper
          elevation={24}
          sx={{
            borderRadius: '8px',
            overflow: 'hidden',
            mb: { xs: 1, md: 2 },
            backgroundColor: '#fff',
            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.3)',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: '40vh', sm: '50vh', md: '60vh' },
            maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 180px)', md: 'calc(100vh - 160px)' },
            position: 'relative',
          }}
        >
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                color: '#666',
                borderRadius: '8px',
                zIndex: 1,
              }}
            >
              Carregando PDF...
            </Box>
          )}
          
          {hasError ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 3,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h5"
                component="h3"
                sx={{
                  color: '#666',
                  mb: 2,
                  fontWeight: 'bold',
                }}
              >
                Não foi possível carregar o PDF
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#888',
                  mb: 3,
                }}
              >
                Use os botões abaixo para acessar o arquivo:
              </Typography>
              
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <PrimaryButton
                  onClick={handleDownload}
                  size="medium"
                  sx={{
                    backgroundColor: '#EF443C',
                    '&:hover': {
                      backgroundColor: '#d73b33',
                    },
                  }}
                >
                  ⬇️ Baixar PDF
                </PrimaryButton>
                
                <SecondaryButton
                  onClick={handleOpenInNewTab}
                  size="medium"
                >
                  🔗 Abrir em Nova Aba
                </SecondaryButton>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                position: 'relative',
              }}
            >
              <iframe
                src="/ThinkWay.pdf#toolbar=1&navpanes=1&scrollbar=1&page=1&view=FitH"
                onLoad={handlePdfLoad}
                onError={handlePdfError}
                title="ThinkWay Pitch Deck PDF"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px',
                }}
              />
            </Box>
          )}
        </Paper>

        <Box
          sx={{
            textAlign: 'center',
            pt: { xs: 0.5, md: 1 },
            borderTop: '1px solid rgba(255, 255, 255, 0.2)',
            mt: 'auto',
            flexShrink: 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.7rem',
            }}
          >
            © 2025 ThinkWay. Planejamento estratégico descomplicado e na ponta dos dedos.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PitchPdf;